import React from 'react';
import { Link } from 'react-router-dom';
import { CookieSettingsLink } from '../shared/cookie/CookieSettingsLink';
import { getImageUrl } from '../../utils/imageUtils';

/**
 * FooterTW - Tailwind-basierter Footer
 *
 * Basiert auf Tailwind UI "Simple centered" Footer
 * - Light/Dark Mode Support
 * - Responsive (Mobile, Tablet, Desktop)
 */

type NavItem = { name: string; href: string; isCookieLink?: boolean };

const navigation: {
  sitemap: Array<{ title: string; items: NavItem[] }>;
  social: Array<{ name: string; href: string; icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element }>;
} = {
  sitemap: [
    {
      title: 'Wissen',
      items: [
        { name: 'Blog', href: '/blog' },
        // Archiv bewusst im Footer: erzeugt von jeder Seite aus einen
        // crawlbaren Pfad zu allen Beiträgen (siehe BlogArchivePageTW).
        { name: 'Blog-Archiv', href: '/blog/archiv' },
        { name: 'Elliott Wave Explorer', href: '/elliott-wave' },
        { name: 'Kontakt', href: '/kontakt' },
      ],
    },
    {
      title: 'Rechtliches',
      items: [
        { name: 'AGB', href: '/agb' },
        { name: 'Cookie-Einstellungen', href: '#cookie-settings', isCookieLink: true },
        { name: 'Datenschutz', href: '/datenschutz' },
        { name: 'Impressum', href: '/impressum' },
        { name: 'Verträge hier kündigen', href: '/kuendigen' },
        { name: 'Widerruf', href: '/widerrufen' },
      ],
    },
  ],
  social: [
    {
      name: 'YouTube',
      href: 'https://youtube.com/@renditefuchs',
      icon: (props: React.SVGProps<SVGSVGElement>) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
    },
    {
      name: 'Discord',
      href: 'https://discord.com/invite/dy2h7ektXS',
      icon: (props: React.SVGProps<SVGSVGElement>) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M20.317 4.369a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.227-1.994a.076.076 0 0 0-.042-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      ),
    },
    {
      name: 'X',
      href: 'https://x.com/renditefuchs',
      icon: (props: React.SVGProps<SVGSVGElement>) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
  ],
};

// Deutsche Flagge als SVG-Komponente
const GermanFlag: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg">
    <rect width="30" height="6.67" fill="#000000"/>
    <rect y="6.67" width="30" height="6.67" fill="#DD0000"/>
    <rect y="13.33" width="30" height="6.67" fill="#FFCE00"/>
  </svg>
);

interface FooterTWProps {
  className?: string;
  style?: React.CSSProperties;
  variant?: 'default' | 'landing';
}

export const FooterTW: React.FC<FooterTWProps> = ({ className, style, variant = 'default' }) => {
  const currentYear = new Date().getFullYear();
  const isLanding = variant === 'landing';

  // Auf der Landingpage Kontakt-Link als Anker zum Formular
  const sitemap = isLanding
    ? navigation.sitemap.map(section => ({
        ...section,
        items: section.items.map(item =>
          item.name === 'Kontakt' ? { ...item, href: '#kontakt' } : item
        ),
      }))
    : navigation.sitemap;

  // Styling-Klassen basierend auf variant
  const linkClass = isLanding
    ? "text-[#b7ab98] hover:text-brand transition-colors duration-150"
    : "text-gray-600 hover:text-brand dark:text-gray-400 dark:hover:text-brand transition-colors duration-150";

  const headingClass = isLanding
    ? "text-sm font-semibold text-[#d9cfbd]"
    : "text-sm font-semibold text-gray-900 dark:text-gray-100";

  const socialClass = isLanding
    ? "text-[#8a8070] hover:text-[#b7ab98] transition-colors duration-150"
    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-150";

  const textClass = isLanding
    ? "text-[#8a8070]"
    : "text-gray-500 dark:text-gray-400";

  const creditClass = isLanding
    ? "text-[#8a8070]"
    : "text-gray-400 dark:text-gray-500";

  return (
    <footer className={className || "bg-gray-100 dark:bg-gray-950"} style={style}>
      <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={getImageUrl("/images/branding/rf-fux-yellow.svg")}
              alt="RENDITEFUCHS Logo"
              className="w-10 h-10 transition-transform group-hover:scale-105 drop-shadow-[1px_1px_1px_rgba(0,0,0,0.4)] dark:drop-shadow-none"
            />
            <span className="text-xl font-bold text-brand [text-shadow:1px_1px_1px_rgba(0,0,0,0.4)] dark:[text-shadow:none]">
              RENDITEFUCHS
            </span>
          </Link>
        </div>

        {/* Sitemap-Spalten */}
        <nav
          aria-label="Footer"
          className="grid grid-cols-1 gap-10 text-sm sm:grid-cols-2 sm:gap-16 sm:justify-items-center"
        >
          {sitemap.map((section) => (
            <div key={section.title} className="flex flex-col items-center sm:items-start">
              <h3 className={`${headingClass} mb-4`}>{section.title}</h3>
              <ul className="flex flex-col items-center sm:items-start gap-y-2">
                {section.items.map((item) => (
                  <li key={item.name}>
                    {item.isCookieLink ? (
                      <CookieSettingsLink
                        className={`${linkClass} min-h-[44px] flex items-center`}
                      />
                    ) : item.href.startsWith('#') ? (
                      <a
                        href={item.href}
                        className={`${linkClass} min-h-[44px] flex items-center`}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className={`${linkClass} min-h-[44px] flex items-center`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Social Icons */}
        <div className="mt-12 flex justify-center gap-x-8 sm:gap-x-10">
          {navigation.social.map((item) => (
            <a
              key={item.name}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${socialClass} min-w-[44px] min-h-[44px] flex items-center justify-center`}
            >
              <span className="sr-only">{item.name}</span>
              <item.icon aria-hidden="true" className="w-6 h-6" />
            </a>
          ))}
        </div>

        {/* Copyright */}
        <p className={`mt-10 text-center text-sm ${textClass}`}>
          &copy; {currentYear} RENDITEFUCHS. Alle Rechte vorbehalten.
        </p>

        {/* Made with Passion, Made in Germany */}
        <p className={`mt-4 text-center text-sm ${textClass} flex items-center justify-center gap-2`}>
          Made with passion. Made in
          <GermanFlag className="w-5 h-4 inline-block" />
        </p>

        {/* Logos Credit */}
        <p className={`mt-4 text-center text-xs ${creditClass}`}>
          <a
            href="https://elbstream.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand transition-colors duration-150"
          >
            Logos provided by Elbstream
          </a>
        </p>
      </div>
    </footer>
  );
};

export default FooterTW;
